import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  bulkImportSkillsApi,
  createCategoryApi,
  createSkillsApi,
  deleteCategoriesApi,
  deleteSkillsApi,
  getAllSkills,
  moveSkillApi,
  updateCategoryApi,
  updateSkillApi,
} from "controller/api/control-center/skills";
import { useEffect, useMemo } from "react";
import type {
  ApiErrorType,
  CreateSkillsBody,
  CreateSkillsPayload,
  Skill,
  SkillsListPage,
  SkillsListResponse,
  UpdateCategorySkillsPayload,
} from "shared";
import { sentryLogger } from "utilz";

const SKILLS_LIST_KEY = ["skills", "list"] as const;
/**
 * Exact cache slot for the **unfiltered** list — i.e. `useSkillsList()` with no
 * `searchQuery`. Mutations read/write this slot for optimistic updates and
 * cache-derived ordering since drag-reorder is disabled while a search is
 * active and only the default list is authoritative across operations. The
 * `SKILLS_LIST_KEY` prefix is still used for invalidation so every cached
 * search slot is refreshed too.
 */
const SKILLS_LIST_DEFAULT_KEY = [...SKILLS_LIST_KEY, ""] as const;

/** Categories fetched per page on the lazily-scrolled Skills screen. */
const SKILLS_PAGE_SIZE = 20;
/**
 * Larger page size for `loadAll` consumers (pickers that need the whole
 * catalog) so it arrives in a single request for any realistic tenant —
 * raised from 100 to 500 on 2026-08-14.
 */
const SKILLS_PAGE_SIZE_ALL = 500;

type SkillsInfinite = InfiniteData<SkillsListPage>;

/**
 * Flatten the paged infinite cache into the single `{ categories,
 * skillsByCategoryId }` shape the screen + mutations work with. Categories are
 * de-duped by id (a refetch can momentarily overlap pages) and every page's
 * skill map is merged.
 */
const mergeSkillPages = (
  pages: Array<SkillsListPage | undefined>,
): SkillsListResponse => {
  const categories: SkillsListResponse["categories"] = [];
  const skillsByCategoryId: SkillsListResponse["skillsByCategoryId"] = {};
  const seen = new Set<string>();
  for (const page of pages) {
    if (!page) continue;
    for (const cat of page.categories) {
      if (seen.has(cat.id)) continue;
      seen.add(cat.id);
      categories.push(cat);
    }
    Object.assign(skillsByCategoryId, page.skillsByCategoryId);
  }
  return { categories, skillsByCategoryId };
};

/** Read the default (unfiltered) list, merged across all loaded pages. */
const readMergedList = (
  queryClient: QueryClient,
): SkillsListResponse | undefined => {
  const infinite = queryClient.getQueryData<SkillsInfinite>(
    SKILLS_LIST_DEFAULT_KEY,
  );
  if (!infinite) return undefined;
  return mergeSkillPages(infinite.pages);
};

/**
 * Apply a per-page transform across every loaded page of the default list.
 * Because a category (and its skills) lives entirely within one page, an
 * optimistic update for a single category/skill only mutates the page that
 * holds it — the same transform is a no-op on the others.
 */
const updateListPages = (
  queryClient: QueryClient,
  updater: (page: SkillsListPage) => SkillsListPage,
) => {
  queryClient.setQueryData<SkillsInfinite>(SKILLS_LIST_DEFAULT_KEY, (prev) =>
    prev ? { ...prev, pages: prev.pages.map(updater) } : prev,
  );
};

/** Cache slot used by `loadAll` consumers (the full-catalog pickers). */
const SKILLS_LIST_ALL_KEY = [...SKILLS_LIST_KEY, "", "all"] as const;

/**
 * Read the eagerly-loaded (`loadAll`) catalog, merged to the flat
 * `{ categories, skillsByCategoryId }` shape. For callers that need the whole
 * catalog **synchronously** right after a `refetchQueries` — e.g. resolving
 * freshly-created master skills back to their real backend ids. Returns
 * `undefined` if the catalog hasn't been loaded.
 */
export const readFullSkillsCatalog = (
  queryClient: QueryClient,
): SkillsListResponse | undefined => {
  const infinite =
    queryClient.getQueryData<SkillsInfinite>(SKILLS_LIST_ALL_KEY);
  return infinite ? mergeSkillPages(infinite.pages) : undefined;
};

/**
 * Lists skill categories with their nested skills.
 *
 * When `searchQuery` is provided, the server returns only matching data
 * (category-name match → all its skills; skill-name match only → just the
 * matching skills). The query key includes the trimmed query so each search
 * gets its own cache slot; `keepPreviousData` keeps the UI from flickering
 * back to a loading state while the user types.
 *
 * Mutations invalidate by the `["skills", "list"]` prefix so every cached
 * search slot is refreshed atomically.
 */
export const useSkillsList = (
  searchQuery?: string,
  options?: {
    /**
     * Eagerly fetch every page so consumers that need the **whole** catalog
     * (e.g. the team-member skills picker — client-side search, per-category
     * "Select all", pre-tick) aren't capped at the first page. The Skills
     * screen leaves this off and loads pages lazily on scroll.
     */
    loadAll?: boolean;
  },
) => {
  const trimmed = searchQuery?.trim() ?? "";
  const loadAll = options?.loadAll ?? false;
  // `loadAll` consumers use a larger page size AND their own cache slot
  // (`…, "all"`) so the bigger page size can't collide with the lazily-paged
  // Skills screen, which shares the same search key. Mutations still target
  // the screen's default slot; the "all" slot refreshes via prefix-invalidation.
  const pageSize = loadAll ? SKILLS_PAGE_SIZE_ALL : SKILLS_PAGE_SIZE;
  const query = useInfiniteQuery({
    queryKey: loadAll
      ? [...SKILLS_LIST_KEY, trimmed, "all"]
      : [...SKILLS_LIST_KEY, trimmed],
    queryFn: ({ pageParam = 1 }) =>
      getAllSkills(trimmed || undefined, pageParam, pageSize),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.categories.length ?? 0),
        0,
      );
      const total = lastPage?.total ?? 0;
      return loaded < total ? allPages.length + 1 : undefined;
    },
    staleTime: 30_000,
    keepPreviousData: true,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllSkills",
      }),
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  // `loadAll` mode: chain through the remaining pages one at a time until the
  // full catalog is in cache. Each fetch flips `hasNextPage`, re-running this.
  useEffect(() => {
    if (loadAll && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [loadAll, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten the paged cache to the single shape the screen + mutations expect.
  const data = useMemo(
    () => mergeSkillPages(query.data?.pages ?? []),
    [query.data],
  );

  return {
    data,
    total: query.data?.pages?.[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/**
 * Create skills — either into an existing category or alongside a brand-new
 * category. Mirrors the project's previous payload shape, but translates to the
 * new POST /v1/skills body (snake_case + per-skill `order`).
 *
 * If `categoryId` is provided we append the new skills at the end (orders
 * computed from the cached category's current skill count).
 *
 * If `newCategoryName` is provided we send `category_name` so the backend
 * creates the category and the skills atomically.
 */
/**
 * Silent, batch-friendly create — POSTs a raw `/v1/skills` body and neither
 * toasts nor invalidates. For callers that create several entries at once and
 * then refetch + show their own feedback (the team-member AddSkills manual
 * flow). Keeps page components off the api layer (pages → hooks → api).
 */
export const useCreateSkillEntries = () =>
  useMutation({
    mutationFn: (body: CreateSkillsBody) => createSkillsApi(body),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "createSkillEntries",
      }),
  });

/**
 * Bulk import skills from a .csv/.xlsx/.xls file. `bulkImportSkillsApi` returns
 * a discriminated result (success | validation errors | message) — the caller
 * renders it. Invalidate the list only when the import actually ran (ok:true).
 * No toast here (house rule — the page toasts).
 */
export const useBulkImportSkills = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => bulkImportSkillsApi(file),
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
      }
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "bulkImportSkillsApi",
      }),
  });
};

export const useCreateSkills = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSkillsPayload) => {
      const cached = readMergedList(queryClient);
      const existing =
        (payload.categoryId &&
          cached?.skillsByCategoryId[payload.categoryId]) ||
        [];
      const baseOrder = existing.length;
      const skills = payload.skillNames.map((name, i) => ({
        name,
        order: baseOrder + i + 1,
      }));
      // New category, no skills → create empty category via /categories endpoint
      if (payload.newCategoryName && skills.length === 0) {
        return createCategoryApi(payload.newCategoryName);
      }
      if (payload.categoryId) {
        return createSkillsApi({ category_id: payload.categoryId, skills });
      }
      if (payload.newCategoryName) {
        return createSkillsApi({
          category_name: payload.newCategoryName,
          skills,
        });
      }
      throw new Error("Either categoryId or newCategoryName must be provided");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "createSkills",
      }),
  });
};

/**
 * Edit-mode drawer save: orchestrates three sets of calls in parallel.
 *
 * 1. DELETE /v1/skills          for `deletedSkillIds`
 * 2. PATCH  /v1/skills/:id      for existing skills whose name actually changed
 * 3. POST   /v1/skills          for new skills (entries without `id`)
 */
export const useUpdateCategorySkills = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateCategorySkillsPayload) => {
      const cached = readMergedList(queryClient);
      const existingForCategory: Skill[] =
        cached?.skillsByCategoryId[payload.categoryId] ?? [];
      const existingById = new Map(existingForCategory.map((s) => [s.id, s]));

      const renames = payload.skills.filter(
        (s): s is { id: string; name: string } => {
          if (!s.id) return false;
          const current = existingById.get(s.id);
          return current ? current.name !== s.name : false;
        },
      );
      const newSkills = payload.skills.filter((s) => !s.id);

      // Compute orders for newly-created skills (append at end, accounting for
      // anything deleted in the same call).
      const remainingCount =
        existingForCategory.length - payload.deletedSkillIds.length;
      const newSkillBodies = newSkills.map((s, i) => ({
        name: s.name,
        order: Math.max(0, remainingCount) + i + 1,
      }));

      const ops: Promise<unknown>[] = [];
      if (payload.categoryName) {
        ops.push(updateCategoryApi(payload.categoryId, payload.categoryName));
      }
      if (payload.deletedSkillIds.length > 0) {
        ops.push(deleteSkillsApi(payload.deletedSkillIds));
      }
      for (const r of renames) {
        ops.push(updateSkillApi(r.id, { name: r.name }));
      }
      if (newSkillBodies.length > 0) {
        ops.push(
          createSkillsApi({
            category_id: payload.categoryId,
            skills: newSkillBodies,
          }),
        );
      }
      // Reorder existing skills via the bulk endpoint — single PATCH carries
      // the full ordered list, backend updates atomically.
      const reorder = payload.reorderedExistingSkills ?? [];
      if (reorder.length > 0) {
        const first = reorder[0];
        ops.push(
          updateSkillApi(first.id, {
            order: first.order,
            skills: reorder.map((s) => ({ skill_id: s.id, order: s.order })),
          }),
        );
      }

      await Promise.all(ops);
      return { categoryId: payload.categoryId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateCategorySkills",
      }),
  });
};

export const useDeleteSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) => deleteSkillsApi([skillId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "deleteSkill",
      }),
  });
};

export const useDeleteSkillCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => deleteCategoriesApi([categoryId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "deleteSkillCategory",
      }),
  });
};

/**
 * Inline single-skill rename — PATCH /v1/skills/:id { name }. Optimistically
 * updates the skill's name in the default list cache so the row reflects the
 * change immediately; rolls back on error. Used by the accordion's inline-edit
 * affordance (gated by `INLINE_RENAME_ENABLED`).
 */
export const useRenameSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; name: string }) =>
      updateSkillApi(payload.id, { name: payload.name }),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: SKILLS_LIST_KEY });
      const previous = queryClient.getQueryData<SkillsInfinite>(
        SKILLS_LIST_DEFAULT_KEY,
      );
      if (!previous) return { previous: undefined };
      updateListPages(queryClient, (page) => ({
        ...page,
        skillsByCategoryId: Object.fromEntries(
          Object.entries(page.skillsByCategoryId).map(([cat, list]) => [
            cat,
            list.map((s) =>
              s.id === payload.id ? { ...s, name: payload.name } : s,
            ),
          ]),
        ),
      }));
      return { previous };
    },
    onError: (error: ApiErrorType, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SKILLS_LIST_DEFAULT_KEY, context.previous);
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "renameSkill",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
  });
};

/**
 * Inline category rename — PATCH /v1/skills/categories/:id { name }.
 * Optimistically updates the category name in the default list cache; rolls
 * back on error (e.g. a 409 when the name already exists — the backend message
 * surfaces via the error toast).
 */
export const useRenameCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; name: string }) =>
      updateCategoryApi(payload.id, payload.name),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: SKILLS_LIST_KEY });
      const previous = queryClient.getQueryData<SkillsInfinite>(
        SKILLS_LIST_DEFAULT_KEY,
      );
      if (!previous) return { previous: undefined };
      updateListPages(queryClient, (page) => ({
        ...page,
        categories: page.categories.map((c) =>
          c.id === payload.id ? { ...c, name: payload.name } : c,
        ),
      }));
      return { previous };
    },
    onError: (error: ApiErrorType, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SKILLS_LIST_DEFAULT_KEY, context.previous);
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "renameCategory",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
  });
};

/**
 * Same-category reorder. Sends a single PATCH to the dragged skill's endpoint
 * carrying the full reordered list of skills in `skills[]`; backend
 * bulk-updates orders atomically. Optimistically updates the cache so the row
 * visibly moves before the server confirms; rolls back on error.
 */
export const useReorderSkillsInCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      categoryId: string;
      draggedSkillId: string;
      newOrder: Array<{ id: string; order: number }>;
    }) => {
      const dragged = payload.newOrder.find(
        (n) => n.id === payload.draggedSkillId,
      );
      if (!dragged) return null;
      return updateSkillApi(payload.draggedSkillId, {
        order: dragged.order,
        skills: payload.newOrder.map((s) => ({
          skill_id: s.id,
          order: s.order,
        })),
      });
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: SKILLS_LIST_KEY });
      const previous = queryClient.getQueryData<SkillsInfinite>(
        SKILLS_LIST_DEFAULT_KEY,
      );
      if (!previous) return { previous: undefined };
      const orderById = new Map(
        payload.newOrder.map((n) => [n.id, n.order] as const),
      );
      updateListPages(queryClient, (page) => {
        const skillsForCategory = page.skillsByCategoryId[payload.categoryId];
        if (!skillsForCategory) return page;
        const reordered = skillsForCategory
          .map((s) => ({ ...s, order: orderById.get(s.id) ?? 0 }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return {
          ...page,
          skillsByCategoryId: {
            ...page.skillsByCategoryId,
            [payload.categoryId]: reordered,
          },
        };
      });
      return { previous };
    },
    onError: (error: ApiErrorType, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SKILLS_LIST_DEFAULT_KEY, context.previous);
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "reorderSkillsInCategory",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
  });
};

/**
 * Cross-category move. Single PATCH /v1/skills/:id/move with the complete
 * final state of both source and target categories. Optimistically updates
 * both category lists in cache; rolls back on error.
 */
export const useMoveSkillToCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      skillId: string;
      sourceCategoryId: string;
      targetCategoryId: string;
      sourceOrder: Array<{ id: string; order: number }>;
      targetOrder: Array<{ id: string; order: number }>;
    }) =>
      moveSkillApi(payload.skillId, {
        source_category: {
          category_id: payload.sourceCategoryId,
          skills: payload.sourceOrder.map((s) => ({
            skill_id: s.id,
            order: s.order,
          })),
        },
        target_category: {
          category_id: payload.targetCategoryId,
          skills: payload.targetOrder.map((s) => ({
            skill_id: s.id,
            order: s.order,
          })),
        },
      }),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: SKILLS_LIST_KEY });
      const previous = queryClient.getQueryData<SkillsInfinite>(
        SKILLS_LIST_DEFAULT_KEY,
      );
      if (!previous) return { previous: undefined };
      // The moved skill may live on a different page than the target category,
      // so resolve it from the merged view and carry it into the per-page
      // updater via closure.
      const merged = mergeSkillPages(previous.pages);
      const movedSkill = (
        merged.skillsByCategoryId[payload.sourceCategoryId] ?? []
      ).find((s) => s.id === payload.skillId);
      if (!movedSkill) return { previous };

      const sourceOrderById = new Map(
        payload.sourceOrder.map((s) => [s.id, s.order] as const),
      );
      const targetOrderById = new Map(
        payload.targetOrder.map((s) => [s.id, s.order] as const),
      );

      updateListPages(queryClient, (page) => {
        const hasSource = payload.sourceCategoryId in page.skillsByCategoryId;
        const hasTarget = payload.targetCategoryId in page.skillsByCategoryId;
        if (!(hasSource || hasTarget)) return page;
        const nextSkills = { ...page.skillsByCategoryId };
        if (hasSource) {
          nextSkills[payload.sourceCategoryId] = (
            page.skillsByCategoryId[payload.sourceCategoryId] ?? []
          )
            .filter((s) => s.id !== payload.skillId)
            .map((s) => ({ ...s, order: sourceOrderById.get(s.id) ?? 0 }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        }
        if (hasTarget) {
          nextSkills[payload.targetCategoryId] = [
            ...(page.skillsByCategoryId[payload.targetCategoryId] ?? []),
            { ...movedSkill, categoryId: payload.targetCategoryId },
          ]
            .map((s) => ({ ...s, order: targetOrderById.get(s.id) ?? 0 }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        }
        return {
          ...page,
          skillsByCategoryId: nextSkills,
          categories: page.categories.map((c) => {
            if (hasSource && c.id === payload.sourceCategoryId)
              return {
                ...c,
                skillCount: nextSkills[payload.sourceCategoryId].length,
              };
            if (hasTarget && c.id === payload.targetCategoryId)
              return {
                ...c,
                skillCount: nextSkills[payload.targetCategoryId].length,
              };
            return c;
          }),
        };
      });
      return { previous };
    },
    onError: (error: ApiErrorType, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SKILLS_LIST_DEFAULT_KEY, context.previous);
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "moveSkillToCategory",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
  });
};

/**
 * "Empty category" = delete every skill inside the category while keeping the
 * category itself. Backend has no dedicated endpoint, so read the current
 * skill ids from cache and bulk-delete them.
 */
export const useEmptySkillCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const cached = readMergedList(queryClient);
      const skillIds = (cached?.skillsByCategoryId[categoryId] ?? []).map(
        (s) => s.id,
      );
      if (skillIds.length === 0) return { deleted_count: 0 };
      return deleteSkillsApi(skillIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_LIST_KEY });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "emptySkillCategory",
      }),
  });
};
